import { Button } from "@base/ui/Button/Button";
import { Dialog } from "@base/ui/Dialog";
import { TextField } from "@base/ui/TextField";
import { PRICE_PER_HALF_HOUR } from "@features/reservation/config/constants";
import { getDurationHours, parseTimeToSlot } from "@features/reservation/lib/utils";
import DayTimeSelector from "@features/reservation/ui/DayTimeSelector";
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import type { ReactNode } from "react";
import { useForm } from "@tanstack/react-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { reservationQueries } from "@features/reservation/api/queryFactory";
import { useQuery } from "@tanstack/react-query";
import { useCreateReservationPayment } from "@features/reservation/model/hooks";
import { minutesToDecimalHours } from "@features/myinfo/lib/utils";
import { membershipQueries } from "@app/config/queryFactory";

function ReservationSummary({
  freeCreditsHours,
  startTime,
  endTime,
}: {
  freeCreditsHours: number;
  startTime: string;
  endTime: string;
}) {
  const { t } = useTranslation();

  const reservingHours = getDurationHours(startTime ?? "", endTime ?? "");

  const isOverFreeCredits = freeCreditsHours - reservingHours < 0;

  const halfHourBlocks = Math.max(
    0,
    Math.round((isOverFreeCredits ? reservingHours - freeCreditsHours : reservingHours) * 2),
  );
  const paidAmount = halfHourBlocks * PRICE_PER_HALF_HOUR;

  return (
    <div className="flex flex-col gap-6 rounded-8">
      <div>
        <h4 className="mb-3 font-semibold text-label1 text-gray-1000">{t("credits")}</h4>
        <div className="flex justify-between mb-2 text-gray-900 text-label2">
          <span>{t("freeCredits")}</span>
          <span>{t("hours", { count: freeCreditsHours })}</span>
        </div>
        <div className="flex justify-between text-gray-900 text-label2">
          <span>{t("reservingHours")}</span>
          <span>{t("hours", { count: Number(reservingHours.toFixed(1)) })}</span>
        </div>
      </div>

      {isOverFreeCredits && (
        <div>
          <h4 className="mb-3 font-semibold text-label1 text-gray-1000">{t("payment")}</h4>
          <div className="flex justify-between mb-2 text-gray-900 text-label2">
            <span>{t("paymentLine", { count: halfHourBlocks })}</span>
            <span>${paidAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mt-1 font-semibold text-label1 text-gray-1000">
            <span>{t("total")}</span>
            <span>${paidAmount.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export interface ReserveRoomDialogProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: number;
  roomName: string;
  date: Date;
}

interface StartTimeFieldProps {
  form: {
    Field: (props: {
      name: "startTime";
      children: (field: {
        state: { value: string };
        handleChange: (v: string) => void;
      }) => ReactNode;
    }) => ReactNode;
    setFieldValue: (name: "endTime", value: string) => void;
  };
  roomId: number;
  dateStr: string;
  t: (key: string) => string;
}

function StartTimeField({ form, roomId, dateStr, t }: StartTimeFieldProps) {
  const { data, isLoading } = useQuery(reservationQueries.availableStartTimes(roomId, dateStr));
  const startTimeSlots = data?.start_times.map(parseTimeToSlot) ?? [];

  return (
    <form.Field name="startTime">
      {(field) => (
        <DayTimeSelector
          items={startTimeSlots}
          value={field.state.value}
          onValueChange={(v) => {
            field.handleChange(v);
            form.setFieldValue("endTime", "");
          }}
          label={t("startTime")}
          placeholder={t("selectTime")}
          required
          disabled={isLoading || startTimeSlots.length === 0}
        />
      )}
    </form.Field>
  );
}

interface EndTimeFieldProps {
  form: {
    Field: (props: {
      name: "endTime";
      children: (field: { state: { value: string }; handleChange: (v: string) => void }) => ReactNode;
    }) => ReactNode;
  };
  roomId: number;
  dateStr: string;
  startTime: string;
  t: (key: string) => string;
}

function EndTimeField({ form, roomId, dateStr, startTime, t }: EndTimeFieldProps) {
  const { data, isLoading } = useQuery(reservationQueries.availableEndTimes(roomId, dateStr, startTime));

  const endTimeSlots = data?.end_times.map(parseTimeToSlot) ?? [];

  return (
    <form.Field name="endTime">
      {(field) => (
        <DayTimeSelector
          items={endTimeSlots}
          value={field.state.value}
          onValueChange={field.handleChange}
          label={t("endTime")}
          placeholder={!startTime || endTimeSlots.length === 0 ? t("selectStartTimeFirst") : t("selectTime")}
          required
          disabled={!startTime || endTimeSlots.length === 0 || isLoading}
        />
      )}
    </form.Field>
  );
}

const ReserveRoomDialog = ({ isOpen, onClose, roomId, roomName, date }: ReserveRoomDialogProps) => {
  const { t } = useTranslation();

  const dateStr = dayjs(date).format("YYYY-MM-DD");
  const dateDisplay = dayjs(date).format("MMM D, YYYY");

  const { mutate, isPending } = useCreateReservationPayment();

  const { data: membership } = useQuery(membershipQueries.membership());
  const { data } = useQuery({
    ...reservationQueries.remainingMinutes(),
    enabled: !!membership,
  });

  const freeCreditsHours = minutesToDecimalHours(data?.remaining_minutes ?? 0);

  const form = useForm({
    defaultValues: {
      title: "",
      startTime: "",
      endTime: "",
    },
    onSubmit: async ({ value }) => {
      if (isPending) return;

      mutate(
        {
          room_id: roomId,
          date: dateStr,
          start_time: value.startTime,
          end_time: value.endTime,
          title: value.title,
        },
        {
          onSettled: () => {
            onClose();
          },
        },
      );
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Dialog.Content className="w-full max-w-[calc(100vw-32px)] md:max-w-[455px]">
        <div className="flex justify-between items-center p-4">
          <Dialog.Title>{t("reservationDialogTitle")}</Dialog.Title>
          <Dialog.CloseTrigger asChild>
            <button type="button" className="p-1 -m-1 hover:bg-gray-100 rounded-4">
              <Icon path={mdiClose} size="1.5rem" />
            </button>
          </Dialog.CloseTrigger>
        </div>

        <p className="px-4 pt-2 text-subtitle4 text-primary-500">
          {roomName} - {dateDisplay}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4 p-4"
        >
          <form.Field
            name="title"
            validators={{
              onChange: ({ value }) => (!value ? t("titleRequired") : undefined),
            }}
          >
            {(field) => (
              <TextField required invalid={!!field.state.meta.errors.length} className="flex flex-col gap-1">
                <TextField.Label>{t("title")}</TextField.Label>
                <TextField.Input
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <TextField.ErrorText>{field.state.meta.errors[0]}</TextField.ErrorText>
                )}
              </TextField>
            )}
          </form.Field>

          <StartTimeField form={form as StartTimeFieldProps["form"]} roomId={roomId} dateStr={dateStr} t={t} />

          <form.Subscribe selector={(state) => state.values.startTime}>
            {(startTime) => (
              <EndTimeField
                form={form as EndTimeFieldProps["form"]}
                roomId={roomId}
                dateStr={dateStr}
                startTime={startTime}
                t={t}
              />
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => [state.values.startTime, state.values.endTime]}>
            {([startTime, endTime]) => (
              <ReservationSummary freeCreditsHours={freeCreditsHours} startTime={startTime} endTime={endTime} />
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => [state.values.title, state.values.startTime, state.values.endTime]}>
            {([title, st, et]) => (
              <Button type="submit" className="w-full" disabled={!title || !st || !et || isPending}>
                {t("reserve")}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};

export default ReserveRoomDialog;
